import { InternationalizedField, Language } from '@/api/common';
import Hint from '@/api/hint';
import Exercise from '@/api/exercise';
import LessonVideoInfo from '@/api/lessonVideoInfo';
import Media from '@/api/media';
import PaddingVideoInfo from '@/api/paddingVideoInfo';
import Phoneme from '@/api/phoneme';
import StressMarker from '@/api/stressMarker';
import TranscriptArpabet from '@/api/transcriptArpabet';
import { isEmpty, isNil } from 'lodash';
import {
  createI18nFields,
  createMediaField,
  createHintField,
  createVideoFrameField,
  createVideoPaddingField
} from '../common/util';

function parseUserSentence(data: { [key: string]: string }) {
  const userSentence = data['user_sentence'];
  if (isNil(userSentence) || isEmpty(userSentence)) {
    return {
      sentence: '',
      transcript_arpabet: []
    };
  }

  const userForceTrans = data['user_force_trans'];
  const userForceFlags = data['user_force_flags'];

  const parts = userSentence.split('>') as string[];
  const forceTransParts = userForceTrans.split('>') as string[];
  const forceFlagsParts = userForceFlags.split('>') as string[];

  console.log(forceTransParts);
  console.log(forceFlagsParts);

  const transcript_arpabet = parts.map((item, index) => {
    const transcript = new TranscriptArpabet();
    transcript.text = item;
    transcript.trans =
      forceTransParts.length > index ? forceTransParts[index].split(',') : [];
    transcript.flags =
      forceFlagsParts.length > index ? forceFlagsParts[index].split(',') : [];

    console.log(transcript);
    return transcript;
  });

  const sentence = parts.join(' ');
  return {
    sentence,
    transcript_arpabet
  };
}

function parseStressMarker(data: { [key: string]: string }, fieldName: string) {
  const raw = data[fieldName];
  const markers = raw.split('>') as string[];

  function findStressIndex(item: string) {
    if (isEmpty(item)) {
      return {
        start: 0,
        end: 0
      };
    }

    const start = item.indexOf('*');
    const end = item.indexOf('*', start + 1);

    if (start < 0 || end < 0 || start > end) {
      return {
        start: 0,
        end: 0
      };
    }

    return {
      start: start,
      end: end - 2
    };
  }

  const stressMarkers = markers.map(findStressIndex);
  return stressMarkers;
}

async function createPhonemesFields(
  data: { [key: string]: string },
  exerciseId: string
) {
  try {
    if (isEmpty(data['user_phonemes'])) {
      return null;
    }

    const phonemes = parseStressMarker(data, 'user_phonemes').map((item) => {
      const phoneme = new Phoneme();
      phoneme.start_index = item.start;
      phoneme.end_index = item.end;
      phoneme.parent_exercise = Number.parseInt(exerciseId);
      return phoneme;
    });

    const jobs = phonemes.map((item) => item.save());
    await Promise.all(jobs);

    return phonemes;
  } catch (err) {
    console.log('createPhonemesFields', err);
    return null;
  }
}

async function createStressMarkerFields(
  data: { [key: string]: string },
  exerciseId: string
) {
  try {
    if (isEmpty(data['user_stress'])) {
      return [];
    }

    const targetWords = parseStressMarker(data, 'user_target_word');
    const markers = parseStressMarker(data, 'user_stress').map(
      (item, index) => {
        const stressMarker = new StressMarker();
        stressMarker.start_index = item.start;
        stressMarker.end_index = item.end;
        stressMarker.prominence = data.prominence;
        stressMarker.duration = data.duration;

        if (targetWords.length > index) {
          stressMarker.word_start_index = targetWords[index].start;
          stressMarker.word_end_index = targetWords[index].end;
        } else {
          stressMarker.word_start_index = 0;
          stressMarker.word_end_index = 0;
        }

        stressMarker.parent_exercise = Number.parseInt(exerciseId);
        return stressMarker;
      }
    );

    const jobs = markers.map((item) => item.save());
    await Promise.all(jobs);

    return markers;
  } catch (err) {
    console.log('createStressMarkerFields', err);
    return null;
  }
}

export async function createSpeakingContent(
  data: { [key: string]: string },
  exercise: Exercise
) {
  if (isNil(exercise.speaking_content)) {
    return;
  }
  const speaking = exercise.speaking_content;
  speaking.parent_exercise = exercise.id;
  speaking.transcription = data['user_transcription'];
  speaking.example = data['user_example'];
  speaking.description = createI18nFields(data, 'user_description', true);

  const sentence = parseUserSentence(data);
  speaking.transcript_arpabet = sentence.transcript_arpabet;
  speaking.sentence = sentence.sentence;
  speaking.sentence_i18n = createI18nFields(data, 'user_sentence_i18n');

  const videoFrames = createVideoFrameField(data, 'user_lesson_video');
  speaking.lesson_video_list = isEmpty(videoFrames) ? [] : videoFrames;
  speaking.lesson_video = isEmpty(videoFrames)
    ? new LessonVideoInfo()
    : videoFrames[0];

  const paddingFrames = createVideoPaddingField(data, 'user_padding_lesson');
  speaking.padding_video_list = isEmpty(paddingFrames) ? [] : paddingFrames;
  speaking.padding_video = isEmpty(paddingFrames)
    ? new PaddingVideoInfo()
    : paddingFrames[0];

  const stressMarkers = await createStressMarkerFields(data, exercise.id);
  const phonenemes = await createPhonemesFields(data, exercise.id);

  if (!isNil(stressMarkers)) {
    speaking.stress_marker_objects = stressMarkers;
    speaking.stress_markers = stressMarkers.map((item) => item.id);
  }

  if (!isNil(phonenemes)) {
    speaking.phoneme_objects = phonenemes;
    speaking.phonemes = phonenemes.map((item) => item.id);
  }

  const id = await speaking.save();

  if (!isEmpty(data['user_audio_path'])) {
    const mediaId = await createMediaField(
      data['user_audio_path'],
      id,
      'exercisespeakingcontent',
      'audio'
    );

    speaking.audio_path = mediaId;
  }

  if (!isEmpty(data['user_image_path'])) {
    console.log('user_image_path', data['user_image_path']);
    const mediaId = await createMediaField(
      data['user_image_path'],
      id,
      'exercisespeakingcontent',
      'image',
      'image'
    );

    speaking.image_path = mediaId;
  }

  speaking.global_hints = []
  speaking.global_hints_objects = await createHintField(data, 'global_hints_i18n', id, 'exercisespeakingcontent');
  
  const arrKeys = Object.keys(speaking.global_hints_objects)
  speaking.global_hints = arrKeys.map(el => speaking.global_hints_objects[el]).filter(Boolean).reduce((result, curr) => {
    result = [...result, ...curr.map(el => el.id)]
    return result
  }, [])

  return speaking.save();
}
